<template>
  <Modal name="EDIT_ITEM_DEFAULT_SERVICE_VALUES" title="Editar">
    <template v-slot:body>
      <v-form ref="editItemDefaultServiceValuesForm">
        <h3 class="mb-6">
          {{ $t('defaultServiceValues.type') }}
        </h3>

        <v-autocomplete
          v-model="type"
          :no-data-text="$t('defaultServiceValues.label.noData')"
          :label="$t('defaultServiceValues.label.serviceType')"
          :items="serviceTypes"
          :loading="isLoading"
          :rules="[rules.required]"
          hide-details="auto"
          hide-selected
          outlined
        >
          <template slot="item" slot-scope="data">
            {{ $t(`type.${data.item.value}`) }}
          </template>
          <template slot="selection" slot-scope="data">
            {{ $t(`type.${data.item.value}`) }}
          </template>
        </v-autocomplete>

        <h3 class="mt-8 mb-3">
          {{ $t('defaultServiceValues.values') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="price"
              :label="$t('defaultServiceValues.label.price')"
              :prefix="$t('currency.symbol')"
              :suffix="$t('currency.decimalType')"
              :rules="[rules.required, rules.amount]"
              class="amount"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="square_meter_price"
              :label="$t('defaultServiceValues.label.squareMetersPrice')"
              :prefix="$t('currency.symbol')"
              :suffix="$t('currency.decimalType')"
              :rules="[rules.required, rules.amount]"
              class="amount"
              hide-details="auto"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="discount"
              :amount="discount"
              :label="$t('defaultServiceValues.label.discount')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="base_square_meters_size"
              :label="$t('defaultServiceValues.label.baseSquareMetersPrice')"
              :prefix="$t('currency.symbol')"
              :suffix="$t('currency.decimalType')"
              :rules="[rules.required, rules.amount]"
              class="amount"
              hide-details="auto"
              outlined
            />
          </v-col>
        </v-row>

        <h3 class="mt-6 mb-3">
          {{ $t('defaultServiceValues.taxes') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_tax"
              :amount="host_tax"
              :label="$t('defaultServiceValues.label.taxHost')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="displacement_tax"
              :amount="displacement_tax"
              :label="$t('defaultServiceValues.label.displacementTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="square_meter_tax"
              :amount="square_meter_tax"
              :label="$t('defaultServiceValues.label.taxSquareMeters')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="furniture_tax"
              :amount="furniture_tax"
              :label="$t('defaultServiceValues.label.furnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="semi_furniture_tax"
              :amount="semi_furniture_tax"
              :label="$t('defaultServiceValues.label.semiFurnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="house_tax"
              :amount="house_tax"
              :label="$t('defaultServiceValues.label.houseTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="trousseau_tax"
              :amount="trousseau_tax"
              :label="$t('defaultServiceValues.label.trousseauTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_realty_feature_tax"
              :amount="host_realty_feature_tax"
              :label="$t('defaultServiceValues.label.hostRealtyFeatureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('defaultServiceValues.label.urgencyTax')"
              v-model="urgency_tax"
              :amount="urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('defaultServiceValues.label.hostUrgencyTax')"
              :amount="host_urgency_tax"
              v-model="host_urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
              :disabled="urgency_tax <= 0"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="urgency_hours_limit"
              :label="$t('defaultServiceValues.label.urgencyHoursLimit')"
              class="amount inputAmount mr-2"
              :rules="urgency_tax > 0 ? [rules.required] : []"
              hide-details="auto"
              suffix="h"
              outlined
              :disabled="urgency_tax <= 0"
            />
          </v-col>
        </v-row>

        <v-divider class="my-5" />

        <v-row>
          <v-col cols="12" sm="6">
            <v-btn
              @click="closeModal('EDIT_ITEM_DEFAULT_SERVICE_VALUES')"
              color="grey"
              elevation="0"
              x-large
              block
              text
            >
              {{ $t('defaultServiceValues.actions.cancel') }}
            </v-btn>
          </v-col>
          <v-col cols="12" sm="6">
            <v-btn
              @click="updateLine"
              color="primary"
              elevation="0"
              x-large
              block
            >
              {{ $t('defaultServiceValues.actions.update') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import { amount, percent, required } from '@/util/errorHandling'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import HAmount from '../../app/HAmount.vue';

export default {
  name: 'EditDefaultServiceValuesModal',

  components: {
    Modal,
    HAmount
  },

  data() {
    return {
      contracts: [],
      isLoading: false,

      type: null,
      price: null,
      discount: null,
      square_meter_price: null,
      base_square_meters_size: null,
      host_tax: null,
      host_realty_feature_tax: null,
      displacement_tax: null,
      square_meter_tax: null,
      furniture_tax: null,
      semi_furniture_tax: null,
      house_tax: null,
      trousseau_tax: null,
      urgency_tax: null,
      host_urgency_tax: null,
      urgency_hours_limit: null,

      rules: {
        amount,
        percent,
        required,
      },
    }
  },

  watch: {
    serviceContract(value) {
      this.type = value.type
      this.price = value.price
      this.discount = value.discount
      this.square_meter_price = value.square_meter_price
      this.base_square_meters_size = value.base_square_meters_size
      this.host_tax = value.host_tax
      this.host_realty_feature_tax = value.host_realty_feature_tax
      this.displacement_tax = value.displacement_tax
      this.square_meter_tax = value.square_meter_tax
      this.furniture_tax = value.furniture_tax
      this.semi_furniture_tax = value.semi_furniture_tax ?? 0
      this.house_tax = value.house_tax ?? 0
      this.trousseau_tax = value.trousseau_tax
      this.urgency_tax = value.urgency_tax
      this.host_urgency_tax = value.host_urgency_tax
      this.urgency_hours_limit = value.urgency_hours_limit
    },

    urgency_tax(tax) {
      if (!tax || tax <= 0) {
        this.host_urgency_tax = 0
        this.urgency_hours_limit = null
      }
    }
  },

  computed: {
    ...mapGetters([
      'serviceTypes',
      'serviceContract',
      'defaultServiceValuesFields',
    ]),
  },

  methods: {
    ...mapActions(['closeModal']),
    ...mapMutations(['setServiceContract', 'setDefaultServiceValuesFields']),

    updateLine() {
      this.contracts = this.defaultServiceValuesFields.service_contracts

      const serviceContract = {
        type: this.type,
        price: this.price,
        discount: this.discount,
        square_meter_price: this.square_meter_price,
        base_square_meters_size: this.base_square_meters_size,
        host_tax: this.host_tax,
        displacement_tax: this.displacement_tax,
        square_meter_tax: this.square_meter_tax,
        furniture_tax: this.furniture_tax,
        semi_furniture_tax: this.semi_furniture_tax,
        house_tax: this.house_tax,
        trousseau_tax: this.trousseau_tax,
        host_realty_feature_tax: this.host_realty_feature_tax,
        urgency_tax: this.urgency_tax,
        host_urgency_tax: this.host_urgency_tax,
        urgency_hours_limit: this.urgency_hours_limit
      }

      const serviceContractUpdated = [
        ...this.contracts.slice(0, this.serviceContract.index),
        serviceContract,
        ...this.contracts.slice(this.serviceContract.index + 1),
      ]

      this.setDefaultServiceValuesFields({
        service_contracts: serviceContractUpdated,
      })

      this.closeModal('EDIT_ITEM_DEFAULT_SERVICE_VALUES')
    },
  },
}
</script>
